/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { AxiosRequestConfig } from 'axios';

import { Endpoints, Infer } from 'shared';

import { ApiClient } from '../types/api/ApiClient';
import { EntityId } from '../types/api/EntityId';
import { ClusterContinuosBackupOldersRestorePoint } from '../types/cluster/ClusterContinuosBackupOldersRestorePoint';
import { ClusterContinuousBackup } from '../types/cluster/ClusterContinuousBackup';
import { ClusterCreateRequestBody } from '../types/cluster/ClusterCreateRequestBody';
import { ClusterCredentialsCypexResponsePayload } from '../types/cluster/ClusterCredentialsCypexResponsePayload';
import { ClusterCredentialsResponsePayload } from '../types/cluster/ClusterCredentialsResponsePayload';
import { ClusterDetailsResponsePayload } from '../types/cluster/ClusterDetailsResponsePayload';
import { ClusterLogicalResponsePayload } from '../types/cluster/ClusterLogicalResponsePayload';
import { ClusterNodeListResponsePayload } from '../types/cluster/ClusterNodeListResponsePayload';
import { ClusterParameterResponse } from '../types/cluster/ClusterParameterResponse';
import { ClusterResponsePayload } from '../types/cluster/ClusterResponsePayload';
import { ClusterStorageResponsePayload } from '../types/cluster/ClusterStorageResponsePayload';
import { ClusterUpdateLifecycleStateRequestBody } from '../types/cluster/ClusterUpdateLifecycleStateRequestBody';
import { ClusterUpdateRequestBody } from '../types/cluster/ClusterUpdateRequestBody';
import { ClusterUserListResponsePayload } from '../types/cluster/ClusterUserListResponsePayload';
import { CreateClusterResponseBody } from '../types/cluster/CreateClusterResponseBody';
import { LogicalBackupStatusResponsePayload } from '../types/cluster/LogicalBackupStatusResponsePayload';
import { PgHbaResponsePayload } from '../types/cluster/PgHba/PgHbaResponsePayload';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';

class ClusterApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  fetchCluster = (props: { organizationId: EntityId; projectId: EntityId; clusterId: EntityId }) => {
    return this.api.get<Infer<typeof Endpoints.GetCluster>['Response']['200']>(Endpoints.GetCluster.route(props));
  };

  fetchClusters = ({ organizationId, projectId }: { organizationId: EntityId; projectId: EntityId }) => {
    return this.api.get<Infer<typeof Endpoints.GetAllCluster>['Response']['200']>(
      Endpoints.GetAllCluster.route({ organizationId, projectId }),
    );
  };

  fetchAdvice = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) =>
    this.api.get<Infer<typeof Endpoints.GetAdvice>['Response']['200']>(
      Endpoints.GetAdvice.route({ organizationId, projectId, serviceId: clusterId }),
    );

  fetchDeepthinkerLatestRuntime = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) =>
    this.api.get<Infer<typeof Endpoints.GetDeepthinkerLatestRuntime>['Response']['200']>(
      Endpoints.GetDeepthinkerLatestRuntime.route({ organizationId, projectId, serviceId: clusterId }),
    );

  triggerDeepthinkerRun = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => this.api.post(Endpoints.PostDeepthinkerRun.route({ organizationId, projectId, serviceId: clusterId }), {});

  fetchDeepthinkerRunStatus = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) =>
    this.api.get<Infer<typeof Endpoints.GetDeepthinkerRunStatus>['Response']['200']>(
      Endpoints.GetDeepthinkerRunStatus.route({ organizationId, projectId, serviceId: clusterId }),
    );

  fetchDismissedAdvice = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) =>
    this.api.get<Infer<typeof Endpoints.GetDismissedAdvice>['Response']['200']>(
      Endpoints.GetDismissedAdvice.route({ organizationId, projectId, serviceId: clusterId }),
    );

  fetchClusterContinuousBackups = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterContinuousBackup[]>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/backup/continuous`,
    );
  };

  fetchClusterLogicalBackups = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterLogicalResponsePayload[]>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/backup/logical`,
    );
  };

  fetchClusterBackupsHistory = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<Infer<typeof Endpoints.GetClusterBackupsHistory>['Response']['200']>(
      Endpoints.GetClusterBackupsHistory.route({ organizationId, projectId, clusterId }),
    );
  };

  fetchClusterPgHba = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<PgHbaResponsePayload[]>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/pg-hba`,
    );
  };

  fetchClusterDatabaseList = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<string[]>(`/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/database`);
  };

  fetchClusterUserList = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterUserListResponsePayload>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/user`,
    );
  };

  createCluster = ({
    projectId,
    data,
    organizationId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    data: ClusterCreateRequestBody;
  }) => {
    return this.api.post<CreateClusterResponseBody>(
      `/organization/${organizationId}/project/${projectId}/cluster`,
      data,
    );
  };

  deleteCluster = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.del(`/organization/${organizationId}/project/${projectId}/cluster/${clusterId}`);
  };

  deleteClusterBackup = ({
    organizationId,
    projectId,
    clusterId,
    name,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    name: string;
  }) => {
    return this.api.del(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/backup/logical/${btoa(name)}`,
    );
  };

  updateCluster = ({
    organizationId,
    projectId,
    clusterId,
    data,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    data: ClusterUpdateRequestBody;
  }) => {
    return this.api.patch<ClusterResponsePayload>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}`,
      data,
    );
  };

  updateClusterLifecycleState = ({
    organizationId,
    projectId,
    clusterId,
    data,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    data: ClusterUpdateLifecycleStateRequestBody;
  }) => {
    return this.api.patch(`/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/lifecycle`, data);
  };

  upgradeClusterPostgresVersion = ({
    organizationId,
    projectId,
    clusterId,
    schedule,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    schedule: boolean;
  }) => {
    return this.api.patch(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/upgrade`,
      {},
      { params: { schedule } },
    );
  };

  fetchClusterDetails = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterDetailsResponsePayload>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/details`,
    );
  };

  fetchClusterStatus = (props: Record<'organizationId' | 'projectId' | 'clusterId', string>) =>
    this.api.get<Infer<typeof Endpoints.GetClusterStatus>['Response'][200]>(Endpoints.GetClusterStatus.route(props));

  fetchClusterUpgradeStatus = (props: { organizationId: EntityId; projectId: EntityId; clusterId: EntityId }) =>
    this.api.get<Infer<typeof Endpoints.GetClusterUpgrade>['Response'][200]>(Endpoints.GetClusterUpgrade.route(props));

  fetchClusterNodeList = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterNodeListResponsePayload[]>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/node`,
    );
  };

  fetchClusterNodeLogs = ({
    organizationId,
    projectId,
    clusterId,
    nodeId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    nodeId: EntityId;
  }) => {
    return this.api.get<string>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/node/${nodeId}/logs`,
    );
  };

  resetClusterPassword = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.post(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/reset-password`,
      {},
    );
  };

  fetchClusterParameters = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterParameterResponse>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/parameter`,
    );
  };

  fetchClusterCredentials = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterCredentialsResponsePayload>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/credentials`,
    );
  };

  fetchClusterCredentialsCypex = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterCredentialsCypexResponsePayload>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/credentials-cypex`,
    );
  };

  fetchClusterStorage = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterStorageResponsePayload>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/storage`,
    );
  };

  getClusterBackupEndpoint = ({
    organizationId,
    projectId,
    clusterId,
    name,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    name: string;
  }) => {
    return `/api/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/backup/logical/file/${btoa(name)}`;
  };

  fetchClusterDatabaseSchemas = ({
    projectId,
    clusterId,
    organizationId,
    databaseName,
  }: {
    projectId: EntityId;
    clusterId: EntityId;
    organizationId: EntityId;
    databaseName: string;
  }) => {
    return this.api.get<string[]>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/database/${databaseName}/schema`,
    );
  };

  createClusterLogicalBackup = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.post(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/backup/logical`,
      {},
    );
  };

  fetchClusterLogicalBackupStatus = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<LogicalBackupStatusResponsePayload>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/backup/logical/status`,
    );
  };

  cancelClusterLogicalBackupCreating = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.del(`/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/backup/logical`);
  };

  getDownloadClusterLogsNodeUrl = ({
    organizationId,
    clusterId,
    projectId,
    nodeId,
    sinceMinutes,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
    nodeId: EntityId;
    sinceMinutes: number;
  }) => {
    return `${getApiEndpoint()}/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/node/${nodeId}/logs/${sinceMinutes}`;
  };

  fetchClusterContinuousBackupLatestRestorePoint = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterContinuosBackupOldersRestorePoint>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/backup/continuous/latest`,
    );
  };

  fetchClusterContinuousBackupOldestRestorePoint = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    return this.api.get<ClusterContinuosBackupOldersRestorePoint>(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/backup/continuous/oldest`,
    );
  };

  restoreClusterFromBackup = (
    {
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    },
    data: { timestamp: string },
  ) => {
    return this.api.post(`/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/restore`, data);
  };

  restartClusterNode = (
    {
      organizationId,
      projectId,
      clusterId,
      nodeId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
      nodeId: EntityId;
    },
    config?: AxiosRequestConfig,
  ) => {
    return this.api.post(
      `/organization/${organizationId}/project/${projectId}/cluster/${clusterId}/node/${nodeId}/restart`,
      {},
      config,
    );
  };

  dismissAdvice = (
    { organizationId, projectId, clusterId }: { organizationId: EntityId; projectId: EntityId; clusterId: EntityId },
    advice: Infer<typeof Endpoints.PutDismissedAdvice>['Body'],
  ) =>
    this.api.put(Endpoints.PutDismissedAdvice.route({ organizationId, projectId, serviceId: clusterId }), {
      info: advice.info,
      type: advice.type,
      severity: advice.severity,
      object: advice.object,
      subObject: advice.subObject,
    });

  clearDismissedAdvice = ({
    organizationId,
    projectId,
    clusterId,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    clusterId: EntityId;
  }) => {
    console.log('here');
    return this.api.del(Endpoints.DeleteDismissedAdvices.route({ organizationId, projectId, serviceId: clusterId }));
  };
}

export const ClusterApiService = new ClusterApiServiceClass();
